import { randomBytes } from 'node:crypto';
import { liveStakingModeForTarget, theoreticalMaxEpisodeExposure } from './live-rules.ts';
import type { PaperStrategyConfig } from './types.ts';

export const CONFIG_TOKEN_TTL_MS = 5 * 60_000;
export const MAX_PAPER_STRATEGIES = 32;

export const CONFIG_LIMITS = {
  minTarget: 2,
  maxTarget: 100,
  minDrought: 1,
  maxDrought: 400,
  maxStopDrought: 500,
  minStake: 0.01,
  maxStake: 50,
  maxMartingaleWagers: 12,
  maxMartingaleExposure: 10_000,
  maxFixedWagers: 100,
  maxFixedExposure: 4_000,
} as const;

export type ConfigSource = 'WEB' | 'TELEGRAM' | 'SYSTEM';
export type ConfigVersionStatus = 'PENDING' | 'ACTIVE' | 'SUPERSEDED';
export type StrategyDiffKind = 'UNCHANGED' | 'CHANGED' | 'ADDED' | 'REMOVED';

export type ConfigControlErrorCode =
  | 'VALIDATION'
  | 'CONFIGURATION_CHANGED'
  | 'NO_CHANGES'
  | 'CONFIRMATION_REQUIRED'
  | 'TOKEN_EXPIRED'
  | 'TOKEN_USED'
  | 'TOKEN_UNKNOWN'
  | 'TOKEN_IDENTITY'
  | 'PENDING_ROUND';

export class ConfigControlError extends Error {
  readonly code: ConfigControlErrorCode;
  readonly status: number;

  constructor(code: ConfigControlErrorCode, message: string, status = 400) {
    super(message);
    this.name = 'ConfigControlError';
    this.code = code;
    this.status = status;
  }
}

export interface StrategyDraftInput {
  id?: string | null;
  targetMultiplier: unknown;
  entryDrought: unknown;
  stopDrought: unknown;
  baseStake: unknown;
}

export interface NormalizedDraft {
  targetMultiplier: number;
  entryDrought: number;
  stopDrought: number;
  baseStake: number;
  stakingMode: PaperStrategyConfig['stakingMode'];
  wagerCount: number;
  maxEpisodeExposure: number;
}

export interface StrategyDiff {
  id: string;
  kind: StrategyDiffKind;
  old: PaperStrategyConfig | null;
  next: PaperStrategyConfig | null;
  oldExposure: number;
  nextExposure: number;
}

export interface ConfigProposal {
  expectedVersion: number;
  strategies: PaperStrategyConfig[];
  diffs: StrategyDiff[];
  added: number;
  removed: number;
  changed: number;
}

export interface ConfigVersionRecord {
  runId: string;
  version: number;
  createdAtUtc: string;
  source: ConfigSource;
  previousVersion: number | null;
  effectiveAfterSequence: number;
  effectiveFromSequence: number | null;
  activatedAtUtc: string | null;
  status: ConfigVersionStatus;
  strategies: PaperStrategyConfig[];
}

export interface ConfigControlView {
  runId: string;
  lastProcessedSequence: number | null;
  unresolvedSequence: number | null;
  active: ConfigVersionRecord;
  pending: ConfigVersionRecord | null;
  history: ConfigVersionRecord[];
}

export interface TelegramConfigToken {
  token: string;
  runId: string;
  chatId: string;
  expectedVersion: number;
  proposed: PaperStrategyConfig[];
  expiresAtUtc: string;
  usedAtUtc: string | null;
}

export function modeForTarget(targetMultiplier: number): PaperStrategyConfig['stakingMode'] {
  return liveStakingModeForTarget(targetMultiplier);
}

export function strategyParamsEqual(left: PaperStrategyConfig, right: PaperStrategyConfig): boolean {
  return (
    left.targetMultiplier === right.targetMultiplier &&
    left.entryDrought === right.entryDrought &&
    left.stopDrought === right.stopDrought &&
    left.stakingMode === right.stakingMode &&
    left.baseStake === right.baseStake
  );
}

export function strategyFingerprint(config: {
  targetMultiplier: number;
  entryDrought: number;
  stopDrought: number;
  stakingMode: PaperStrategyConfig['stakingMode'];
  baseStake: number;
}): string {
  return `${config.targetMultiplier}|${config.entryDrought}|${config.stopDrought}|${config.stakingMode}|${config.baseStake}`;
}

export function formatStrategyId(id: string): string {
  return id.startsWith('#') ? id : `#${id}`;
}

export function compactStrategyLine(config: PaperStrategyConfig): string {
  return `${formatStrategyId(config.id)} ${config.targetMultiplier}x D${config.entryDrought}-S${config.stopDrought} $${formatStake(config.baseStake)}`;
}

export function formatStake(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return String(Number(value.toFixed(4)));
}

export function formatExposure(value: number): string {
  if (Number.isInteger(value)) {
    return `$${value}`;
  }
  return `$${value.toFixed(2)}`;
}

export function exposureFor(config: PaperStrategyConfig): number {
  return theoreticalMaxEpisodeExposure(config);
}

export function parseFiniteNumber(value: unknown, label: string): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  throw new ConfigControlError('VALIDATION', `${label} must be a finite number.`);
}

export function parseInteger(value: unknown, label: string): number {
  const parsed = parseFiniteNumber(value, label);
  if (!Number.isInteger(parsed)) {
    throw new ConfigControlError('VALIDATION', `${label} must be an integer.`);
  }
  return parsed;
}

export function normalizeDraft(input: StrategyDraftInput, label: string): NormalizedDraft {
  const targetMultiplier = parseFiniteNumber(input.targetMultiplier, `${label} target`);
  const entryDrought = parseInteger(input.entryDrought, `${label} D`);
  const stopDrought = parseInteger(input.stopDrought, `${label} S`);
  const baseStake = parseFiniteNumber(input.baseStake, `${label} stake`);
  if (targetMultiplier < CONFIG_LIMITS.minTarget) {
    throw new ConfigControlError('VALIDATION', `${label}: target must be ≥ ${CONFIG_LIMITS.minTarget}.`);
  }
  if (targetMultiplier > CONFIG_LIMITS.maxTarget) {
    throw new ConfigControlError('VALIDATION', `${label}: target must be ≤ ${CONFIG_LIMITS.maxTarget}.`);
  }
  if (entryDrought < CONFIG_LIMITS.minDrought) {
    throw new ConfigControlError('VALIDATION', `${label}: D must be ≥ ${CONFIG_LIMITS.minDrought}.`);
  }
  if (entryDrought > CONFIG_LIMITS.maxDrought) {
    throw new ConfigControlError('VALIDATION', `${label}: D must be ≤ ${CONFIG_LIMITS.maxDrought}.`);
  }
  if (stopDrought > CONFIG_LIMITS.maxStopDrought) {
    throw new ConfigControlError('VALIDATION', `${label}: S must be ≤ ${CONFIG_LIMITS.maxStopDrought}.`);
  }
  if (stopDrought < entryDrought + 1) {
    throw new ConfigControlError('VALIDATION', `${label}: S must be ≥ D + 1.`);
  }
  if (!(baseStake > 0) || baseStake < CONFIG_LIMITS.minStake) {
    throw new ConfigControlError('VALIDATION', `${label}: stake must be > 0.`);
  }
  if (baseStake > CONFIG_LIMITS.maxStake) {
    throw new ConfigControlError('VALIDATION', `${label}: stake must be ≤ ${CONFIG_LIMITS.maxStake}.`);
  }
  const stakingMode = modeForTarget(targetMultiplier);
  const wagerCount = stopDrought - entryDrought;
  const draftConfig: PaperStrategyConfig = {
    id: typeof input.id === 'string' && input.id.trim() ? input.id.trim() : 'draft',
    targetMultiplier,
    entryDrought,
    stopDrought,
    stakingMode,
    baseStake,
  };
  const maxEpisodeExposure = exposureFor(draftConfig);
  if (stakingMode === 'MARTINGALE') {
    if (wagerCount > CONFIG_LIMITS.maxMartingaleWagers) {
      throw new ConfigControlError(
        'VALIDATION',
        `${label}: Martingale losing wagers ${wagerCount} exceeds safety bound ${CONFIG_LIMITS.maxMartingaleWagers}.`,
      );
    }
    if (maxEpisodeExposure > CONFIG_LIMITS.maxMartingaleExposure) {
      throw new ConfigControlError(
        'VALIDATION',
        `${label}: Martingale max episode exposure ${formatExposure(maxEpisodeExposure)} exceeds safety bound ${formatExposure(CONFIG_LIMITS.maxMartingaleExposure)}.`,
      );
    }
  } else {
    if (wagerCount > CONFIG_LIMITS.maxFixedWagers) {
      throw new ConfigControlError(
        'VALIDATION',
        `${label}: Fixed wagers ${wagerCount} exceeds safety bound ${CONFIG_LIMITS.maxFixedWagers}.`,
      );
    }
    if (maxEpisodeExposure > CONFIG_LIMITS.maxFixedExposure) {
      throw new ConfigControlError(
        'VALIDATION',
        `${label}: Fixed max episode exposure ${formatExposure(maxEpisodeExposure)} exceeds safety bound ${formatExposure(CONFIG_LIMITS.maxFixedExposure)}.`,
      );
    }
  }
  return {
    targetMultiplier,
    entryDrought,
    stopDrought,
    baseStake,
    stakingMode,
    wagerCount,
    maxEpisodeExposure,
  };
}

export function generateStrategyId(used: Set<string>): string {
  for (let attempt = 0; attempt < 64; attempt += 1) {
    const id = randomBytes(2).toString('hex').toUpperCase();
    if (!used.has(id) && !used.has(id.toLowerCase())) {
      used.add(id);
      return id;
    }
  }
  throw new ConfigControlError('VALIDATION', 'Could not allocate a unique strategy id.');
}

export function assembleStrategies(
  current: readonly PaperStrategyConfig[],
  inputs: readonly StrategyDraftInput[],
  options: { allowEmpty?: boolean } = {},
): PaperStrategyConfig[] {
  if (inputs.length === 0) {
    if (options.allowEmpty) {
      return [];
    }
    throw new ConfigControlError(
      'VALIDATION',
      'Empty replace is refused. Use /config clear to remove all strategies.',
    );
  }
  if (inputs.length > MAX_PAPER_STRATEGIES) {
    throw new ConfigControlError('VALIDATION', `At most ${MAX_PAPER_STRATEGIES} strategies are allowed.`);
  }
  const currentById = new Map(current.map((row) => [row.id, row]));
  const usedIds = new Set(current.map((row) => row.id));
  const fingerprints = new Set<string>();
  const assembled: PaperStrategyConfig[] = [];
  for (let index = 0; index < inputs.length; index += 1) {
    const label = `Strategy ${index + 1}`;
    const draft = inputs[index]!;
    const normalized = normalizeDraft(draft, label);
    const fingerprint = strategyFingerprint(normalized);
    if (fingerprints.has(fingerprint)) {
      throw new ConfigControlError('VALIDATION', `${label}: exact duplicate strategies are not allowed.`);
    }
    fingerprints.add(fingerprint);
    const requestedId = typeof draft.id === 'string' ? draft.id.trim().replace(/^#/, '') : '';
    let id: string;
    if (requestedId) {
      if (assembled.some((row) => row.id === requestedId)) {
        throw new ConfigControlError('VALIDATION', `${label}: strategy id ${formatStrategyId(requestedId)} is listed more than once.`);
      }
      const existing = currentById.get(requestedId);
      if (!existing) {
        throw new ConfigControlError('VALIDATION', `${label}: unknown strategy id ${formatStrategyId(requestedId)}.`);
      }
      id = existing.id;
    } else {
      id = generateStrategyId(usedIds);
    }
    assembled.push({
      id,
      targetMultiplier: normalized.targetMultiplier,
      entryDrought: normalized.entryDrought,
      stopDrought: normalized.stopDrought,
      stakingMode: normalized.stakingMode,
      baseStake: normalized.baseStake,
    });
  }
  return assembled;
}

export function buildProposal(
  current: readonly PaperStrategyConfig[],
  expectedVersion: number,
  inputs: readonly StrategyDraftInput[],
  options: { allowEmpty?: boolean } = {},
): ConfigProposal {
  const strategies = assembleStrategies(current, inputs, options);
  const currentById = new Map(current.map((row) => [row.id, row]));
  const nextIds = new Set(strategies.map((row) => row.id));
  const diffs: StrategyDiff[] = [];
  let added = 0;
  let removed = 0;
  let changed = 0;
  for (const next of strategies) {
    const old = currentById.get(next.id) ?? null;
    if (!old) {
      added += 1;
      diffs.push({
        id: next.id,
        kind: 'ADDED',
        old: null,
        next,
        oldExposure: 0,
        nextExposure: exposureFor(next),
      });
      continue;
    }
    const same = strategyParamsEqual(old, next);
    if (!same) {
      changed += 1;
    }
    diffs.push({
      id: next.id,
      kind: same ? 'UNCHANGED' : 'CHANGED',
      old,
      next,
      oldExposure: exposureFor(old),
      nextExposure: exposureFor(next),
    });
  }
  for (const old of current) {
    if (nextIds.has(old.id)) {
      continue;
    }
    removed += 1;
    diffs.push({
      id: old.id,
      kind: 'REMOVED',
      old,
      next: null,
      oldExposure: exposureFor(old),
      nextExposure: 0,
    });
  }
  if (added === 0 && removed === 0 && changed === 0 && sameOrder(current, strategies)) {
    throw new ConfigControlError('NO_CHANGES', 'No strategy changes to apply.');
  }
  return { expectedVersion, strategies, diffs, added, removed, changed };
}

function sameOrder(current: readonly PaperStrategyConfig[], next: readonly PaperStrategyConfig[]): boolean {
  if (current.length !== next.length) {
    return false;
  }
  return current.every((row, index) => row.id === next[index]?.id);
}

export function draftsFrom(configs: readonly PaperStrategyConfig[]): StrategyDraftInput[] {
  return configs.map((row) => ({
    id: row.id,
    targetMultiplier: row.targetMultiplier,
    entryDrought: row.entryDrought,
    stopDrought: row.stopDrought,
    baseStake: row.baseStake,
  }));
}

export function parseTelegramStrategyLine(line: string): StrategyDraftInput {
  const cleaned = line
    .replace(/\$/g, '')
    .replace(/\b(MARTINGALE|MART|FIXED|FIX)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  const match = cleaned.match(/^(?:#(\S+)\s+)?(\d+(?:\.\d+)?)x\s+D(\d+)(?:\s+|-)S(\d+)\s+(\d+(?:\.\d+)?)$/i);
  if (!match) {
    throw new ConfigControlError(
      'VALIDATION',
      `Unrecognized strategy line: ${line.trim() || '(empty)'}. Use: [#ID] 20x D40 S46 1`,
    );
  }
  return {
    id: match[1] ? match[1] : null,
    targetMultiplier: match[2],
    entryDrought: match[3],
    stopDrought: match[4],
    baseStake: match[5],
  };
}

export function parseTelegramConfigCommand(text: string): {
  action: 'replace' | 'clear';
  drafts: StrategyDraftInput[];
} {
  const trimmed = text.replace(/^\s*\/config(?:@\S+)?\s*/i, '').trim();
  if (!trimmed) {
    throw new ConfigControlError('VALIDATION', 'Usage: /config replace … or /config clear');
  }
  const verbMatch = trimmed.match(/^(\S+)([\s\S]*)$/);
  const verb = (verbMatch?.[1] ?? '').toLowerCase();
  const rest = (verbMatch?.[2] ?? '').trim();
  if (verb === 'clear') {
    if (rest) {
      throw new ConfigControlError('VALIDATION', 'Usage: /config clear');
    }
    return { action: 'clear', drafts: [] };
  }
  const body = verb === 'replace' ? rest : trimmed;
  if (verb !== 'replace' && !/^(?:#\S+\s+)?\d/.test(trimmed)) {
    throw new ConfigControlError('VALIDATION', 'Usage: /config replace … or /config clear');
  }
  if (!body) {
    throw new ConfigControlError(
      'VALIDATION',
      'Empty replace is refused. Use /config clear to remove all strategies.',
    );
  }
  const lines = body
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    throw new ConfigControlError(
      'VALIDATION',
      'Empty replace is refused. Use /config clear to remove all strategies.',
    );
  }
  return { action: 'replace', drafts: lines.map((line) => parseTelegramStrategyLine(line)) };
}

export function assertExpectedVersion(activeVersion: number, expectedVersion: number): void {
  if (!Number.isInteger(expectedVersion) || expectedVersion !== activeVersion) {
    throw new ConfigControlError(
      'CONFIGURATION_CHANGED',
      'CONFIGURATION CHANGED. Refresh and review again.',
      409,
    );
  }
}

export function effectiveAfterSequence(
  lastProcessedSequence: number | null,
  unresolvedSequence: number | null,
  startSequence: number,
): number {
  if (unresolvedSequence != null) {
    return unresolvedSequence;
  }
  if (lastProcessedSequence != null) {
    return lastProcessedSequence;
  }
  return startSequence - 1;
}

export function formatTelegramConfigView(view: ConfigControlView): string {
  const lines = [`CONFIG v${view.active.version}`, ''];
  if (view.active.strategies.length === 0) {
    lines.push('(no strategies)');
  } else {
    for (const row of view.active.strategies) {
      lines.push(compactStrategyLine(row));
    }
  }
  if (view.pending) {
    lines.push('');
    lines.push(`Pending: v${view.pending.version} after seq ${view.pending.effectiveAfterSequence}`);
  }
  return lines.join('\n');
}

export function formatTelegramReplacePreview(input: {
  expectedVersion: number;
  proposal: ConfigProposal;
  effectiveAfterSequence: number;
  token: string;
  clear?: boolean;
}): string {
  const lines = input.clear
    ? ['CLEAR CONFIG', '', 'This removes all strategies.', `v${input.expectedVersion} -> v${input.expectedVersion + 1}`]
    : [
        'REPLACE CONFIG',
        '',
        `v${input.expectedVersion} -> v${input.expectedVersion + 1}`,
        `Configured: ${input.proposal.strategies.length}`,
      ];
  lines.push('');
  for (const diff of input.proposal.diffs) {
    if (diff.kind === 'UNCHANGED' && diff.next) {
      lines.push(`= ${compactStrategyLine(diff.next)}`);
    } else if (diff.kind === 'CHANGED' && diff.old && diff.next) {
      lines.push(`~ ${compactStrategyLine(diff.old)} -> ${compactStrategyLine(diff.next)}`);
    } else if (diff.kind === 'ADDED' && diff.next) {
      lines.push(`+ ${compactStrategyLine(diff.next)}`);
    } else if (diff.kind === 'REMOVED' && diff.old) {
      lines.push(`- ${compactStrategyLine(diff.old)}`);
    }
  }
  lines.push('');
  lines.push(`Effective after seq ${input.effectiveAfterSequence}`);
  lines.push('');
  lines.push('Reply:');
  lines.push(`/confirm ${input.token}`);
  lines.push('');
  lines.push('or:');
  lines.push(`/cancel ${input.token}`);
  return lines.join('\n');
}
